import React from 'react';
import cn from 'bem-cn-lite';
import some_ from 'lodash/some';
import isEmpty_ from 'lodash/isEmpty';

import ellipsisIcon from '../../assets/img/svg/ellipsis.svg';

import {useDispatch, useSelector} from '../../store/redux-hooks';

import {PagesSettingsEditor, usePagesMenuItems} from './PagesSettingsEditor';
import {
    getClusterConfigByName,
    selectAuthWay,
    selectCurrentUserName,
    selectRootPagesCluster,
} from '../../store/selectors/global';

import {useClusterFromLocation} from '../../hooks/use-cluster';

import './AppNavigation.scss';
import UIFactory from '../../UIFactory';
import {type AppNavigationProps} from './AppNavigationPageLayout';
import {selectSettingNavigationPanelExpanded} from '../../store/selectors/settings';
import {setSettingNavigationPanelExpanded} from '../../store/actions/settings/settings';
import {setAsideHeaderWidth} from '../../store/actions/global';
import {SettingsPanelLazy} from '../SettingsPanel/lazy';
import i18n from './i18n';

const block = cn('app-navigation');

interface ExtProps {
    children: React.ReactNode;
}

function isRootPage(cluster = '') {
    return some_(UIFactory.getExtraRootPages(), (info) => {
        return cluster === info.pageId;
    });
}

export default function AppNavigation({children}: ExtProps) {
    const dispatch = useDispatch();
    const pageOrCluster = useClusterFromLocation();
    const rootPageCluster = useSelector(selectRootPagesCluster);
    const cluster = isRootPage(pageOrCluster) ? rootPageCluster : pageOrCluster;
    const clusterConfig = getClusterConfigByName(cluster || '');

    const items = usePagesMenuItems(cluster);
    const currentUser = useSelector(selectCurrentUserName);
    const authWay = useSelector(selectAuthWay);

    const [settingsVisible, setSettingsVisible] = React.useState(false);
    const [panelVisible, setPanelVisible] = React.useState(false);

    const onClosePanel = React.useCallback(() => {
        setSettingsVisible(false);
        setPanelVisible(false);
    }, []);

    const togglePanelVisibility = React.useCallback(() => {
        setSettingsVisible(false);
        setPanelVisible((visible) => !visible);
    }, []);

    const toggleSettingsVisible = React.useCallback(() => {
        setPanelVisible(false);
        setSettingsVisible((visible) => !visible);
    }, []);

    const menuItems: typeof items = React.useMemo(() => {
        if (!cluster) {
            return [];
        }
        return [
            ...items,
            {
                id: 'all_pages',
                title: i18n('title_all-pages'),
                icon: ellipsisIcon,
                current: false,
                onItemClick: togglePanelVisibility,
                iconSize: 24,
            },
        ];
    }, [items, cluster, togglePanelVisibility]);

    const className = block();

    const expanded = useSelector(selectSettingNavigationPanelExpanded);
    const onChangeCompact = React.useCallback(
        (newCompact: boolean) => {
            dispatch(setSettingNavigationPanelExpanded(!newCompact));

            setTimeout(() => {
                const el = window.document.querySelector(`.${className}`);
                if (el) {
                    const widthStr =
                        getComputedStyle(el)?.getPropertyValue('--gn-aside-header-size');
                    const width = Number(widthStr?.slice?.(0, -2)); // '236px' or '56px'
                    if (!isNaN(width)) {
                        dispatch(setAsideHeaderWidth(width));
                    }
                }
            }, 500);
        },
        [dispatch, className],
    );

    const props: AppNavigationProps = {
        className,
        clusterConfig,
        logoClassName: block('logo-icon', {'no-cluster': !cluster}),
        currentUser,
        authWay,
        menuItems: isEmpty_(clusterConfig) ? [] : menuItems,
        panelContent: (
            <PagesSettingsEditor
                cluster={cluster}
                className={block('panel-content')}
                onItemClick={onClosePanel}
            />
        ),
        panelClassName: block('panel'),
        panelVisible,
        onClosePanel,
        settingsContent: <SettingsPanelLazy />,
        settingsVisible,
        toggleSettingsVisible,

        compact: !expanded,
        onChangeCompact,
        children,
    };

    return UIFactory.renderAppNavigation(props);
}
